import { useState, useCallback, useRef, useEffect } from 'react';
import { Room } from 'twilio-video';
import { ErrorCallback } from '../../../types';

export default function useScreenShareToggle(
	room: Room | null,
	onError: ErrorCallback,
) {
	const [isSharing, setIsSharing] = useState(false);
	const state = useRef<'idle' | 'starting' | 'sharing'>('idle');
	const generation = useRef(0);
	const stop = useRef<() => void>(() => {});
	useEffect(() => {
		const cleanup = () => {
			generation.current++;
			stop.current();
			stop.current = () => {};
			state.current = 'idle';
			setIsSharing(false);
		};
		room?.on('disconnected', cleanup);
		return () => {
			room?.off('disconnected', cleanup);
			cleanup();
		};
	}, [room]);
	const toggleScreenShare = useCallback(() => {
		if (!room || room.state === 'disconnected') return;
		if (state.current === 'sharing') {
			stop.current();
			return;
		}
		if (state.current === 'starting') return;
		state.current = 'starting';
		const attempt = generation.current;
		const active = () =>
			generation.current === attempt && room.state !== 'disconnected';
		void (async () => {
			let stream: MediaStream | undefined;
			let publication: any;
			let stopped = false;
			const cleanup = () => {
				if (stopped) return;
				stopped = true;
				stream?.getTracks().forEach((track) => {
					track.onended = null;
					track.stop();
				});
				if (publication) {
					room.localParticipant.unpublishTrack(publication.track);
					room.localParticipant.emit('trackUnpublished', publication);
				}
				if (active()) {
					state.current = 'idle';
					setIsSharing(false);
				}
			};
			try {
				stream = await navigator.mediaDevices.getDisplayMedia({
					audio: false,
					video: true,
				});
				if (!active()) {
					cleanup();
					return;
				}
				const track = stream.getVideoTracks()[0];
				if (!track)
					throw new Error(
						'No screen was selected. Please try again.',
					);
				stop.current = cleanup;
				track.onended = cleanup;
				publication = await room.localParticipant.publishTrack(track, {
					name: 'screen',
					priority: 'low',
				});
				if (!active() || stopped) {
					room.localParticipant.unpublishTrack(publication.track);
					cleanup();
					return;
				}
				state.current = 'sharing';
				setIsSharing(true);
			} catch (error) {
				cleanup();
				if (
					active() &&
					((error as Error).message ===
						'Permission denied by system' ||
						!['AbortError', 'NotAllowedError'].includes(
							(error as Error).name,
						))
				)
					onError(
						new Error(
							(error as Error).message ===
								'Permission denied by system'
								? 'Allow screen recording in your system settings, then try sharing again.'
								: 'Screen sharing could not start. Please try again.',
						),
					);
			}
		})();
	}, [room, onError]);
	return [isSharing, toggleScreenShare] as const;
}
